<template>
    <div
        :class="[computedType === 'body-data' ? 'w-1/3 border border-gray-300 rounded bg-gray-50' : 'w-full border border-gray-300 rounded bg-gray-50 flex flex-nowrap']"
    >
        <control-icon
            v-if="icon"
            class="flex-none relative"
            :icon="icon"
            :h="controlIconH"
        />
        <select
            v-if="computedType === 'select'"
            :id="id"
            v-model="computedValue"
            :name="name"
            :class="inputElClass"
            :required="required"
            :disabled="disabled"
            class="flex-initial bg-gray-50"
        >
            <option
                v-for="option in options"
                :key="option.value ?? option"
                :value="option.value ?? option"
            >
                {{ option.name ?? option }}
            </option>
        </select>
        <textarea
            v-else-if="computedType === 'textarea'"
            :id="id"
            v-model="computedValue"
            :class="inputElClass"
            :name="name"
            :placeholder="placeholder"
            :required="required"
            :disabled="disabled"
            :readonly="readonly"
            :minlength="minlength"
            :maxlength="maxlength"
            class="flex-initial bg-gray-50"
        />
        <textarea
            v-else-if="computedType === 'textarea-bg-white'"
            :id="id"
            v-model="computedValue"
            :class="inputElClass"
            :name="name"
            :placeholder="placeholder"
            :required="required"
            :disabled="disabled"
            :readonly="readonly"
            :minlength="minlength"
            :maxlength="maxlength"
            class="flex-initial bg-white"
            style="height: 20rem;"
        />
        <input
            v-else-if="computedType === 'body-data'"
            :id="id"
            ref="inputEl"
            v-model="computedValue"
            :name="name"
            :autocomplete="autocomplete"
            :required="required"
            :disabled="disabled"
            :readonly="readonly"
            :minlength="minlength"
            :maxlength="maxlength"
            :min="min"
            :max="max"
            :step="step"
            :pattern="pattern"
            :placeholder="placeholder"
            :type="computedType"
            :class="bodyDataClass"
            :inputmode="inputmode"
            class="flex-initial h-6 font-sm"
            :title="title"
            style="background-color: #fefce8; color: #00AF5B;"
            @focus="$event.target.select()"
        >
        <input
            v-else
            :id="id"
            ref="inputEl"
            v-model="computedValue"
            :name="name"
            :autocomplete="autocomplete"
            :required="required"
            :disabled="disabled"
            :readonly="readonly"
            :minlength="minlength"
            :maxlength="maxlength"
            :min="min"
            :max="max"
            :step="step"
            :pattern="pattern"
            :placeholder="placeholder"
            :type="computedType"
            :class="inputElClass"
            :inputmode="inputmode"
            class="flex-initial bg-gray-50"
            :title="title"
        >
        <label
            v-if="label"
            class="flex-none px-3 py-3 font-bold text-sm md:text-base"
        >{{ label }}</label>
    </div>
</template>

<script>
import ControlIcon from '@/components/Utilities/ControlIcon'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

export default {
    name: 'Control',
    components: {
        ControlIcon
    },
    props: {
        name: String,
        label: String,
        id: String,
        required: Boolean,
        autocomplete: String,
        placeholder: String,
        readonly: Boolean,
        disabled: Boolean,
        icon: String,
        options: Array,
        type: {
            type: String,
            default: 'text'
        },
        modelValue: {
            type: [String, Number, Boolean, Array, Object],
            default: ''
        },
        borderless: Boolean,
        transparent: Boolean,
        ctrlKFocus: Boolean,
        min: Number,
        max: Number,
        minlength: Number,
        maxlength: Number,
        pattern: String,
        step: Number,
        inputmode: String,
        title: String
    },
    emits: ['update:modelValue', 'right-icon-click'],
    setup (props, { emit }) {
        const computedValue = computed({
            get: () => props.modelValue,
            set: value => {
                emit('update:modelValue', value)
            }
        })

        const inputElClass = computed(() => {
            const base = [
                'px-3 py-2 max-w-full focus:outline-none required:outline-none border-gray-300 rounded w-full',
                'dark:placeholder-gray-400',
                computedType.value === 'textarea' ? 'h-60' : 'h-12',
                'border-0',
                // props.borderless ? 'border-0' : 'border',
                props.transparent ? 'bg-transparent' : 'bg-white dark:bg-gray-800'
            ]
            return base
        })

        const bodyDataClass = computed(() => {
            const base = [
                'px-1 py-2 max-w-full focus:outline-none required:outline-none w-full text-center font-bold'
            ]
            return base
        })

        const computedType = computed(() => props.options ? 'select' : props.type)

        const controlIconH = computed(() => props.type === 'textarea' ? 'h-full' : 'h-12')

        const store = useStore()

        const inputEl = ref(null)

        if (props.ctrlKFocus) {
            const fieldFocusHook = e => {
                if (e.ctrlKey && e.key === 'k') {
                    e.preventDefault()
                    inputEl.value.focus()
                } else if (e.key === 'Escape') {
                    inputEl.value.blur()
                }
            }

            onMounted(() => {
                if (!store.state.isFieldFocusRegistered) {
                    window.addEventListener('keydown', fieldFocusHook)

                    store.commit('basic', {
                        key: 'isFieldFocusRegistered',
                        value: true
                    })
                } else {
                    console.error('Duplicate field focus event')
                }
            })

            onBeforeUnmount(() => {
                window.removeEventListener('keydown', fieldFocusHook)

                store.commit('basic', {
                    key: 'isFieldFocusRegistered',
                    value: false
                })
            })
        }

        return {
            computedValue,
            inputElClass,
            computedType,
            controlIconH,
            inputEl,
            bodyDataClass
        }
    }
}
</script>

<style scoped>
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type=number] {
    -moz-appearance: textfield;
}
</style>
